import { Link } from 'react-router'
import { Mail, MapPin, Phone } from 'lucide-react'
import { useNavLinks, useSettings, useSponsors } from '@/lib/queries'
import { Container, Crest, MeshTexture } from '@/components/site/primitives'
import { SOCIAL_LINKS } from '@/components/site/social-icons'
import { cn } from '@/lib/utils'

export function SiteFooter() {
  const { data: settings } = useSettings()
  const { data: links = [] } = useNavLinks()
  const { data: sponsors = [] } = useSponsors()

  const year = new Date().getFullYear()
  const clubName = settings?.club_name ?? 'AFC Vikings Cairo'

  const socials = SOCIAL_LINKS.map(({ key, label, Icon }) => ({
    label,
    Icon,
    href: settings?.[key] ?? '',
  })).filter((social) => social.href)

  return (
    <footer className="border-hairline bg-ink-sunken relative overflow-hidden border-t">
      <MeshTexture opacity={0.05} />

      {sponsors.length > 0 ? (
        <div className="border-hairline relative border-b">
          <Container className="py-10">
            <p className="eyebrow text-bone-faint mb-6">Our partners</p>
            <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
              {sponsors.map((sponsor) => {
                const content = (
                  <span
                    className={cn(
                      'condensed text-lg font-semibold tracking-[0.1em] transition-colors',
                      sponsor.tier === 'principal' ? 'text-bone' : 'text-bone-faint',
                      sponsor.url && 'hover:text-blood-bright'
                    )}
                  >
                    {sponsor.name}
                  </span>
                )
                return sponsor.url ? (
                  <a key={sponsor.id} href={sponsor.url} target="_blank" rel="noreferrer noopener">
                    {content}
                  </a>
                ) : (
                  <span key={sponsor.id}>{content}</span>
                )
              })}
            </div>
          </Container>
        </div>
      ) : null}

      <Container className="relative py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1.2fr]">
          <div>
            <Crest className="size-16" />
            <p className="display text-bone mt-5 text-3xl leading-none">{clubName}</p>
            {settings?.footer_blurb ? (
              <p className="text-bone-dim mt-4 max-w-sm text-sm leading-relaxed">
                {settings.footer_blurb}
              </p>
            ) : null}

            {socials.length > 0 ? (
              <div className="mt-7 flex gap-2">
                {socials.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={label}
                    className="border-hairline text-bone-dim hover:border-blood hover:text-blood-bright flex size-10 items-center justify-center border transition-colors"
                  >
                    <Icon className="size-4" aria-hidden />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <nav aria-label="Footer">
            <p className="eyebrow text-blood mb-5">Explore</p>
            <ul className="space-y-3">
              {links.map((link) => (
                <li key={link.id}>
                  <Link
                    to={link.path}
                    className="condensed text-bone-dim hover:text-bone text-[0.9375rem] tracking-[0.1em] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="eyebrow text-blood mb-5">Find us</p>
            <ul className="text-bone-dim space-y-4 text-sm">
              {settings?.address ? (
                <li className="flex gap-3">
                  <MapPin className="text-bone-faint mt-0.5 size-4 shrink-0" aria-hidden />
                  <span className="leading-relaxed">{settings.address}</span>
                </li>
              ) : null}
              {settings?.contact_email ? (
                <li className="flex gap-3">
                  <Mail className="text-bone-faint mt-0.5 size-4 shrink-0" aria-hidden />
                  <a
                    href={`mailto:${settings.contact_email}`}
                    className="hover:text-bone transition-colors"
                  >
                    {settings.contact_email}
                  </a>
                </li>
              ) : null}
              {settings?.contact_phone ? (
                <li className="flex gap-3">
                  <Phone className="text-bone-faint mt-0.5 size-4 shrink-0" aria-hidden />
                  <a
                    href={`tel:${settings.contact_phone.replace(/\s/g, '')}`}
                    className="hover:text-bone transition-colors"
                  >
                    {settings.contact_phone}
                  </a>
                </li>
              ) : null}
            </ul>

            {settings?.training_times ? (
              <div className="border-blood mt-7 border-l-2 pl-4">
                <p className="eyebrow text-bone-faint mb-2">Training</p>
                <p className="text-bone-dim text-sm leading-relaxed">{settings.training_times}</p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="border-hairline mt-14 flex flex-col gap-4 border-t pt-7 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-bone-faint text-xs tracking-wide">
            © {year} {clubName}. All rights reserved.
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {/* The studio credit. "3BONT" is always set in capitals. */}
            <a
              href="https://3bont.com"
              target="_blank"
              rel="noreferrer noopener"
              className="group text-bone-faint hover:text-bone inline-flex items-center gap-2 transition-colors"
            >
              <span className="eyebrow">Powered by</span>
              <span className="condensed text-[0.9375rem] font-bold tracking-[0.14em]">3BONT</span>
            </a>

            <Link
              to="/cms"
              className="eyebrow text-bone-faint hover:text-bone-dim transition-colors"
            >
              Club login
            </Link>
          </div>
        </div>
      </Container>
    </footer>
  )
}
